import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { MdDeleteOutline, MdImageSearch } from "react-icons/md";

interface FileCardProps {
  filename: string;
  filesize: number;
  onDeleteClick: () => void;
  onClickNameFunction?: () => void;
  onSearchImageClick?: () => void;
  initialAnimation?: React.ComponentProps<typeof motion.div>["initial"];
  exitAnimation?: React.ComponentProps<typeof motion.div>["exit"];
}

const getFileTypeDisplay = (filename: string) => {
  if (filename.endsWith(".sb3")) {
    return "Scratch";
  } else if (filename.endsWith(".py")) {
    return "Python";
  } else if (filename.endsWith(".js")) {
    return "JavaScript";
  } else if (filename.endsWith(".pdf")) {
    return "PDF";
  } else if (filename.endsWith(".png")) {
    return "PNG";
  } else if (filename.endsWith(".jpeg")) {
    return "JPEG";
  } else if (filename.endsWith(".gif")) {
    return "GIF";
  }
  return filename;
};

const FileCard: React.FC<FileCardProps> = ({
  filename,
  filesize,
  onDeleteClick,
  onClickNameFunction,
  onSearchImageClick,
  initialAnimation,
  exitAnimation,
}) => {
  // States definition
  const [openFile, setOpenFile] = useState(false);
  const [showOpenImageButton, setShowOpenImageButton] = useState(false);
  const [unit, setUnit] = useState<"B" | "KB" | "MB" | "GB">("B");
  const [filesizeText, setFilesizeText] = useState("0");

  /**
   * Function used to define the behaviour of the
   * name displayed on this card
   */
  useEffect(() => {
    // Get the type of the file
    const filetype = getFileTypeDisplay(filename);

    // Set the state to define if it's opening a new window
    // when clicking the name
    if (filetype === "PDF") setOpenFile(true);
    if (["JPEG", "PNG", "GIF"].includes(filetype)) setShowOpenImageButton(true);

    // And set the size of the file
    const fileSizeKB = filesize / 1024;
    const fileSizeMB = fileSizeKB / 1024;

    // Format to display file size
    if (fileSizeMB >= 1) {
      setFilesizeText(fileSizeMB.toFixed(1)); // toFixed returns a string
      setUnit("MB");
    } else if (fileSizeKB >= 1) {
      setFilesizeText(fileSizeKB.toFixed(1)); // toFixed returns a string
      setUnit("KB");
    } else {
      setFilesizeText(filesize.toFixed(1)); // toFixed returns a string
      setUnit("B");
    }
  }, []);

  /**
   * Function to apply on click name
   */
  const onClickName = () => {
    // Execute the function just if it's allowed
    if (openFile && onClickNameFunction) {
      onClickNameFunction();
    }
  };

  return (
    <motion.div
      initial={initialAnimation}
      animate={{ opacity: 1, y: 0 }}
      exit={exitAnimation}
      transition={{ ease: "easeInOut", duration: 0.5 }}
      className="self-stretch px-3 py-4 rounded-xl border border-Subtle justify-between items-center flex gap-3"
    >
      <div className="inline-flex flex-col justify-start items-start gap-1 flex-grow min-w-0 overflow-hidden">
        <div
          className={`text-neutral-700 text-base font-normal font-sans leading-[19px] truncate w-full ${
            openFile ? "cursor-pointer hover:underline" : ""
          }`}
          onClick={onClickName}
        >
          {filename}
        </div>
        <div className="self-stretch justify-start items-start gap-2 inline-flex">
          <div className="justify-start items-start gap-1 flex">
            <div className="text-neutral-500 text-xs font-normal font-sans leading-[14px]">
              File type:
            </div>
            <div className="text-neutral-500 text-xs font-normal font-sans leading-[14px]">
              {getFileTypeDisplay(filename)}
            </div>
          </div>
          {filesize > 0 && (
            <>
              <div className="w-px self-stretch justify-start items-start gap-2 flex">
                <div className="w-px self-stretch bg-neutral-200" />
              </div>
              <div className="grow shrink basis-0 h-3.5 justify-start items-start gap-1 flex">
                <div className="text-neutral-500 text-xs font-normal font-poppins leading-[14px]">
                  File size:
                </div>
                <div className="text-neutral-500 text-xs font-normal font-poppins leading-[14px]">
                  {filesizeText} {unit}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Show image button */}
      {showOpenImageButton && (
        <div
          className="p-2 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-start items-start gap-2 flex cursor-pointer"
          onClick={onSearchImageClick}
        >
          <MdImageSearch className="w-6 h-6 fill-neutral-600" />
        </div>
      )}
      {/* Delete button */}
      <div
        className="p-2 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-start items-start gap-2 flex cursor-pointer"
        onClick={onDeleteClick}
      >
        <MdDeleteOutline className="w-6 h-6 fill-neutral-600" />
      </div>
    </motion.div>
  );
};

export default FileCard;
